import { cosemApi } from '@services/providers/cosem';
import { AgendaProductTable, ProductTable, ProductToCreate } from './product.types';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import { Product } from './product.types';
import { ActiveFilter } from '@utils/filters';
import { CdnFile } from '@services/core/cdn/file/file';

const productApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getProducts: build.mutation<ApiPaginationResponse<ProductTable>, { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return { 
                    url: `/product/products`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "", filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<ProductTable>) => response
        }),
        getAgendaProducts: build.mutation<ApiPaginationResponse<AgendaProductTable>, { limit?: number; page?: number; search?: string; orderBy?: string }>({
            query: ({ limit = 10, page = 1, search, orderBy }) => {
                return { 
                    url: `/product/agenda-products`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "" }
                };
            },
            transformResponse: (response: ApiPaginationResponse<AgendaProductTable>) => response
        }),
        getProduct: build.mutation<ApiResponse<Product>, { id: string|number }>({
            query: ({ id }) => {
                return { 
                    url: `/product/product/${id}`, 
                    method: 'GET',
                };
            },
            transformResponse: (response: ApiResponse<Product>) => response
        }),
        postProducts: build.mutation<ApiResponse<Product>, ProductToCreate>({
            query: (product: ProductToCreate) => {
                return { 
                    url: `/product/product`, 
                    method: 'POST',
                    body: product
                };
            },
            transformResponse: (response: ApiResponse<Product>) => response
        }),
        putProducts: build.mutation<ApiResponse<Product>, { id: string|number; product: ProductToCreate }>({
            query: ({ id, product }) => {
                return { 
                    url: `/product/product/${id}`, 
                    method: 'PUT',
                    body: product    
                };
            },
            transformResponse: (response: ApiResponse<Product>) => response
        }),

        deleteProduct: build.mutation<ApiResponse<void>, { id: string|number }>({
            query: ({ id }) => {
                return { 
                    url: `/product/product/${id}`, 
                    method: 'DELETE'
                };
            },  
            transformResponse: (response: ApiResponse<void>) => response
        }),

        toggleProductField: build.mutation<ApiResponse<void>, { id: string|number; field: 'isFeatured' | 'isDisabled' }>({
            query: ({ id, field }) => {
                return { 
                    url: `/product/product/${id}/toggle`,
                    body: {
                        field: field
                    },
                    method: 'PUT'
                };
            },
            transformResponse: (response: ApiResponse<void>) => response
        }),

        duplicateProduct: build.mutation<ApiResponse<Product>, { id: string|number }>({
            query: ({ id }) => {
                return { 
                    url: `/product/product/${id}/duplicate`, 
                    method: 'POST'
                };
            },
            transformResponse: (response: ApiResponse<Product>) => response
        }),

        /* Documents */
        getProductDocuments: build.mutation<ApiResponse<CdnFile[]>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/product/product/${id}/documents`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<CdnFile[]>) => response
        }),

        deleteProductDocument: build.mutation<ApiResponse<void>, { id: string; documentId: string }>({
            query: ({ id, documentId }) => {
                return {
                    url: `/product/product/${id}/document/${documentId}`,
                    method: 'DELETE'
                };
            },
            transformResponse: (response: ApiResponse<void>) => response
        }),

        postProductDocument: build.mutation<ApiResponse<CdnFile>, { id: string; file: CdnFile }>({
            query: ({ id, file }) => {
                return {
                    url: `/product/product/${id}/document`,
                    method: 'POST',
                    body: file
                };
            },
            transformResponse: (response: ApiResponse<CdnFile>) => response
        }),
    })
});

export const { 
    useGetProductsMutation,
    useGetAgendaProductsMutation,
    useGetProductMutation,
    usePostProductsMutation,
    usePutProductsMutation,
    useToggleProductFieldMutation,
    useDeleteProductMutation,
    useDuplicateProductMutation,
    useGetProductDocumentsMutation,
    useDeleteProductDocumentMutation,
    usePostProductDocumentMutation
} = productApi;
